import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import { OrderSideEnum, TimeInForceEnum } from '../../../lib/enums';
import { OrderTypeEnum } from '../../../lib/enums/order-type.enum';
import lang from '../../../lib/language';
import { Order } from '../../../lib/models/trading/types';
import { RootState } from '../../../lib/store';
import { getCurrentAccount } from '../../../lib/store/crm/helpers';
import { getOpenPositions as getPositions, setHasOrderUpdate } from '../../../lib/store/reporting';
import { OpenPosition, PositionType } from '../../../lib/store/reporting/types';
import { getCurrentBuyingPower } from '../../../lib/store/selectors';
import MarketStateCache from '../../../lib/store-util/MarketStateCache';
import NBBOPriceCache from '../../../lib/store-util/NBBOPriceCache';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency } from '../../../lib/util/DataHelpers';
import { isMarketClosed, isMarketOpen, isMarketRegularHours } from '../../../lib/util/MarketDataHelpers';
import { TextHelpers } from '../../../lib/util/TextHelpers';
import { uuidv4 } from '../../../lib/util/tools';
import { getOrderTotalValue } from '../../../lib/util/TradingValidationsHelper';

import { useTradingButtonsDisabled } from '../../../hooks';
import CommonDropdown from '../../../shared_components/CommonDropdown/CommonDropdown';
import { Error, NotEnoughBuyingPower, ReviewOrder, ShortSellOrderPlace } from '../..';
import HighLowLimitPriceAlert from '../../HighLowLimitPriceAlert/HighLowLimitPriceAlert';
import SharesCalculator from '../../SharesCalculator/SharesCalculator';
import TradingButtons from '../../TradingButtons/TradingButtons';
import LimitPriceInput from '../components/LimitPriceInput';
import MarketPriceInfo from '../components/MarketPriceInfo';
import { OrderTypeDropdownOptions } from '../order.constants';
import { getNameByOrderType, getValidationPrice, validateOrder } from '../OrderHelpers';

import styles from './CreateOrder.module.scss';

type OwnProps = {
  symbol: string;
  positionsQuantity: number | null;
  isSymbolHalted: boolean;
  hasMyStocksOrderUpdate?: boolean;
  setOrderInfo: (order: Order) => void;
  setFromReview: (fromReview: boolean) => void;
}
type Props = OwnProps & ReduxProps;

function CreateTicketOrder(props: Props) {
  const {
    symbol = '',
    account,
    positionsQuantity,
    buyingPow,
    hasMyStocksOrderUpdate,
    accountOpenPositionsCompleted,
    positions,
    isSymbolHalted,
    setOrderInfo,
    setFromReview,
    myStocksUpdate,
    getOpenPositions,
    accountStatusData,
  } = props;
  const { marketState } = MarketStateCache.use();
  const { currentPrice: tradePrice = 0 } = TradePriceCache.use(symbol, 'CreateOrder');
  const isMarketHours = isMarketRegularHours(marketState);
  const showMarketInfoIcon = isMarketOpen(marketState);

  const [order, setOrder] = useState<Order>({});
  const [reviewOrder, setReviewOrder] = useState<boolean>(false);
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [targetPriceError, setTargetPriceError] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [targetPrice, setTargetPrice] = useState<number>(0);
  const [orderType, setOrderType] = useState<string>(isMarketHours ? OrderTypeEnum.Market : OrderTypeEnum.Limit);
  const [timeInForce, setTimeInForce] = useState<string>(TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay));
  const [visibleWarningShortSellDialog, setVisibleWarningShortSellDialog] = useState<boolean>(false);
  const [isReviewOrderCheckTriggered, setIsReviewOrderCheckTriggered] = useState<boolean>(false);
  const [openedEnoughBPModal, setOpenedEnoughBPModal] = useState<boolean>(false);
  const [visibleHighLowLimitPriceAlert, setVisibleHighLowLimitPriceAlert] = useState<boolean>(false);
  const [buyBtnClicked, setBuyBtnClicked] = useState<boolean>(false);
  const [sellBtnClicked, setSellBtnClicked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [controlCheck, setControlCheck] = useState<boolean>(true);
  const [numberOfSharesError, setNumberOfSharesError] = useState<string>('');
  const nbboData = NBBOPriceCache.use(symbol);
  const { askprice, bidprice } = nbboData;
  const positionsBySymbol = positions.find((position: OpenPosition) => (position.symbol === symbol));
  const isBuyOrder = positionsBySymbol?.positionType === PositionType.Buy;
  const marketClosed = isMarketClosed(marketState);
  const isMarketOrder = orderType === OrderTypeEnum.Market;
  const orderTypeOptions = OrderTypeDropdownOptions(isBuyOrder);
  const orderTypeDropdownOptions = orderTypeOptions.map(option => ({
    id: String(option.value),
    label: option.text ?? '',
    icon: option.icon,
    subText: option.subText,
  }));
  const isButtonsDisabled = useTradingButtonsDisabled({
    symbol,
    accountStatusData,
    additionalCondition: quantity <= 0,
  });
  const isDropdownDisabled = useTradingButtonsDisabled({
    symbol,
    accountStatusData,
  });

  const totalValue = getOrderTotalValue(
    orderType,
    quantity,
    tradePrice,
    targetPrice,
  );

  const isBuyingPowerEnough = buyingPow > totalValue;

  useEffect(() => {
    if (isReviewOrderCheckTriggered && ((accountOpenPositionsCompleted && !marketClosed) || marketClosed)) {
      setFromReview(true);
      setReviewOrder(false);
      setIsReviewOrderCheckTriggered(false);
    }
  }, [order, setFromReview, isReviewOrderCheckTriggered, accountOpenPositionsCompleted, marketClosed]);

  const setOrderData = useCallback((theOrder: Order) => {
    setOrder(theOrder);
    setOrderInfo(theOrder);
    setReviewOrder(true);
  }, [setOrderInfo]);

  const reviewTicketOrder = useCallback((side: string) => {
    const price = tradePrice ?? 0;
    const orderObject: Order = {};
    const isBuySide = side === OrderSideEnum.Buy;
    setTargetPriceError('');
    setNumberOfSharesError('');

    if (orderType === OrderTypeEnum.Stop) {
      orderObject.stopPrice = targetPrice;
    }
    orderObject.clientOrderId = uuidv4();
    orderObject.symbol = symbol;
    orderObject.side = side as OrderSideEnum;
    orderObject.orderQty = quantity;
    orderObject.ordType = orderType as OrderTypeEnum;
    if (orderType !== OrderTypeEnum.Market) {
      orderObject.price = targetPrice;
    }
    orderObject.account = account;

    if (orderType === OrderTypeEnum.Limit && askprice && bidprice) {
      if (
        (isBuySide && (targetPrice > askprice))
        || (!isBuySide && (targetPrice < bidprice))
      ) {
        setVisibleHighLowLimitPriceAlert(isBuySide ? (targetPrice > askprice) : (targetPrice < bidprice));
        setOrder(orderObject);
        setOrderInfo(orderObject);

        return;
      }
    }

    if (!positionsBySymbol && isMarketRegularHours(marketState) && side === OrderSideEnum.Sell) {
      setVisibleWarningShortSellDialog(true);
      setOrder(orderObject);
      setOrderInfo(orderObject);

      return;
    }

    setOrderData(orderObject);
  }, [
    bidprice,
    askprice,
    account,
    isBuyOrder,
    orderType,
    quantity,
    marketState,
    setOrderData,
    setOrderInfo,
    symbol,
    targetPrice,
    timeInForce,
    tradePrice,
    positionsBySymbol,
  ]);

  const handleChangeOrderType = useCallback((option) => {
    setTargetPriceError('');
    setOrderType(option.value);
    setTimeInForce(TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay));
  }, []);

  useEffect(() => {
    if (!isInitialised && positionsQuantity) {
      setIsInitialised(true);
    }
  }, [isInitialised, positionsQuantity]);

  const validateBuyingPower = useCallback((buyingPower: number, isSellBtnClicked = false) => {
    const buyingPowerCheck = buyingPower > totalValue;

    return controlCheck
      && !buyingPowerCheck
      && ((isSellBtnClicked && !isBuyOrder) || (!isSellBtnClicked && (isBuyOrder || !positionsBySymbol)));
  }, [controlCheck, isBuyOrder, positionsBySymbol, totalValue]);

  const areInputFieldsValid = useCallback((validCheck, posQty, curQty, isBuyPos, sellClicked, buyClicked, errMsg) => {
    let hasValid = true;
    if (posQty && curQty > posQty) {
      if ((isBuyPos && sellClicked) || (!isBuyPos && buyClicked)) {
        hasValid = false;
        setNumberOfSharesError(lang.commonNotEnoughSharesAvailable());
      }
    }
    if (!validCheck) {
      hasValid = false;
      setTargetPriceError(errMsg);
    }

    return hasValid;
  }, []);

  useEffect(() => {
    if ((sellBtnClicked || buyBtnClicked)) {
      if (areInputFieldsValid(
        controlCheck, positionsQuantity, quantity, isBuyOrder, sellBtnClicked, buyBtnClicked, errorMessage,
      )) {
        if ((validateBuyingPower(buyingPow) && buyBtnClicked)
          || (validateBuyingPower(buyingPow, true) && sellBtnClicked)) {
          setOpenedEnoughBPModal(true);
        } else {
          reviewTicketOrder(buyBtnClicked ? OrderSideEnum.Buy : OrderSideEnum.Sell);
        }
      }
      setBuyBtnClicked(false);
      setSellBtnClicked(false);
    }
  }, [
    buyBtnClicked,
    buyingPow,
    controlCheck,
    isBuyOrder,
    marketState,
    positionsBySymbol,
    positionsQuantity,
    quantity,
    errorMessage,
    reviewTicketOrder,
    sellBtnClicked,
    areInputFieldsValid,
    validateBuyingPower,
  ]);

  const onSellClicked = () => {
    setSellBtnClicked(true);
    const validationPirce = getValidationPrice(marketClosed, askprice, bidprice, tradePrice);
    validateOrder(validationPirce ?? 0, targetPrice, orderType, false, setControlCheck, setErrorMessage);
  };

  const onBuyClicked = () => {
    setBuyBtnClicked(true);
    const validationPirce = getValidationPrice(marketClosed, askprice, bidprice, tradePrice, true);
    validateOrder(validationPirce ?? 0, targetPrice, orderType, true, setControlCheck, setErrorMessage);
  };

  const proceedShortSellOrderPlaceModal = () => {
    setVisibleWarningShortSellDialog(false);
    setReviewOrder(true);
  };

  const closeShortSellOrderPlaceModal = () => {
    setVisibleWarningShortSellDialog(false);
  };

  const proceedHighLowLimitPriceAlert = () => {
    setVisibleHighLowLimitPriceAlert(false);
    if (!positionsBySymbol && isMarketRegularHours(marketState) && order?.side === OrderSideEnum.Sell) {
      setVisibleWarningShortSellDialog(true);
    } else {
      setReviewOrder(true);
    }
  };

  return (
    <>
      {visibleWarningShortSellDialog && (
        <ShortSellOrderPlace
          toggleModal={closeShortSellOrderPlaceModal}
          processModal={proceedShortSellOrderPlaceModal}
        />
      )}
      <div className={styles.wrapperOrderContainer}>
        <div className={styles.wrapperOrderTypeDropDown}>
          <p className={styles.orderTypeText}>{lang.commonTradingOrderType()}</p>
          <div className={styles.orderTypeDropDown}>
            <CommonDropdown
              options={orderTypeDropdownOptions}
              selectedOption={{ id: String(orderType), label: getNameByOrderType(String(orderType)) }}
              onSelectOption={option => handleChangeOrderType({ value: option.id })}
              title={lang.commonTradingOrderType()}
              isDisabled={isDropdownDisabled || isSymbolHalted}
              menuClassName={styles.orderTypeDropdownMenu}
              itemClassName={styles.orderTypeOption}
            />
          </div>
        </div>
        <div className={styles.wrapperNumberOfShares}>
          <div className={styles.numberOfShares}>
            <p className={styles.numberOfSharesText}>{lang.mobileTradingTicketNumberOfShares()}</p>
            {!!positionsQuantity && (
              <p className={styles.numberOfSharesSubText}>
                {
                  isBuyOrder
                    ? lang.webTradingAvailableShares(positionsQuantity)
                    : lang.webTradingShortedStocks(positionsQuantity)
                }
              </p>
            )}
          </div>
          <div className={styles.wrapperSharesField}>
            <SharesCalculator
              disabled={isSymbolHalted}
              quantity={quantity}
              setQuantity={setQuantity}
              clearNumberOfSharesError={setNumberOfSharesError}
            />
          </div>
        </div>
        {numberOfSharesError && (numberOfSharesError.trim() !== '') && (
          <div className={styles.errorWrapper}>
            <Error hiddenIcon errorMessage={numberOfSharesError} />
          </div>
        )}
        {(!isMarketOrder) && (
          <LimitPriceInput
            disabled={isSymbolHalted}
            initialPrice={0}
            orderType={orderType}
            targetPriceError={targetPriceError}
            setTargetPrice={(value) => setTargetPrice(value)}
            setTargetPriceError={(value) => setTargetPriceError(value)}
          />
        )}
        <MarketPriceInfo
          askPrice={askprice}
          bidPrice={bidprice}
          symbol={symbol}
          tradePrice={tradePrice}
          isMarketOpen={showMarketInfoIcon}
          isSymbolDetailsBigScreen
        />
        {(!isMarketOrder && (
          <div className={styles.totalValueContainer}>
            <div className={styles.textKey}>
              {lang.headerMyAccountPendingOrderExpiryDate2()}
            </div>
            <div className={styles.rightAligned}>
              {TextHelpers.getTimeInForceName(TimeInForceEnum.EndOfDay)}
            </div>
          </div>
        ))}
        <div className={styles.horizontalLine} />
        <div className={styles.totalValueContainer}>
          <div className={styles.textKey}>{lang.commonTradingOrderValue()}</div>
          <div className={classNames(styles.value, { [styles.redValue]: !isBuyingPowerEnough })}>
            {formatCurrency(totalValue)}
          </div>
        </div>
        <div className={styles.availableBuyingContainer}>
          <div className={styles.textKey}>{lang.mobileOrderCreateBuyingPower()}</div>
          <div className={styles.value}>{formatCurrency(buyingPow)}</div>
        </div>
        <TradingButtons
          isSymbolHalted={isSymbolHalted}
          isButtonsDisabled={isButtonsDisabled}
          onSellClicked={onSellClicked}
          onBuyClicked={onBuyClicked}
        />
        {openedEnoughBPModal && <NotEnoughBuyingPower closeModal={() => setOpenedEnoughBPModal(false)} />}
        {visibleHighLowLimitPriceAlert && (
          <HighLowLimitPriceAlert
            isBuyOrder={order?.side === OrderSideEnum.Buy}
            processModal={proceedHighLowLimitPriceAlert}
            toggleModal={() => setVisibleHighLowLimitPriceAlert(false)}
          />
        )}
      </div>
      {reviewOrder && (
        <ReviewOrder
          symbol={symbol}
          order={order}
          isBuyOrder={order.side === OrderSideEnum.Buy}
          onSubmit={() => {
            if (!marketClosed) {
              getOpenPositions(symbol);
              hasMyStocksOrderUpdate && myStocksUpdate(true);
            }
            setIsReviewOrderCheckTriggered(true);
          }}
          onCancel={() => setReviewOrder(false)}
        />
      )}
    </>
  );
}

const mapStateProps = (state: RootState) => ({
  positions: state.reporting.openPositions,
  account: getCurrentAccount(state),
  accountOpenPositionsCompleted: state.reporting.accountOpenPositionsCompleted,
  buyingPow: getCurrentBuyingPower(state),
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getOpenPositions: (symbol: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CreateTicketOrder);
